/**
 * AI Health report schemas.
 *
 * Backend: `backend/src/handlers/admin.ts` -
 *   GET /api/admin/ai-assessment/report            -> AiHealthReport
 *   GET /api/admin/ai-assessment/company?opcoId=   -> AiHealthCompany
 *
 * Shallow on purpose: the contract types in `backend/src/ai-health/report.ts`
 * are the full source of truth. This guards the seams the page cannot render
 * without (identity, status and scope shape); numeric detail passes through.
 */
import { z } from 'zod';

const CycleRefSchema = z.looseObject({ id: z.string(), label: z.string(), dueOn: z.string() });

const CompanyRowSchema = z.looseObject({
  opcoId: z.string(),
  name: z.string(),
  group: z.string(),
  groupSlug: z.string(),
  status: z.string(),
  assessmentStatus: z.string(),
});

const ScopeSummarySchema = z.looseObject({
  companyCount: z.number(),
  kpis: z.looseObject({}),
  statusCounts: z.record(z.string(), z.number()),
  coverage: z.looseObject({}),
  missing: z.array(z.string()),
  talkFirst: z.array(z.string()),
  dims: z.array(z.looseObject({ name: z.string(), avg: z.number().nullable() })),
  series: z.looseObject({}).nullable(),
  cycles: z.array(z.looseObject({ cycleId: z.string() })),
});

export const AiHealthReportSchema = z.looseObject({
  asOf: z.string(),
  cycle: z.looseObject({
    current: CycleRefSchema.nullable(),
    notPublished: CycleRefSchema.nullable(),
    all: z.array(CycleRefSchema),
  }),
  points: z.array(z.looseObject({ label: z.string() })).nullable(),
  companies: z.array(CompanyRowSchema),
  portfolio: ScopeSummarySchema,
  groups: z.array(ScopeSummarySchema.extend({ name: z.string(), slug: z.string(), rank: z.number() })),
  dataQuality: z.looseObject({}),
});

export const AiHealthCompanySchema = z.looseObject({
  opcoId: z.string(),
  opcoName: z.string(),
  operatingGroup: z.string(),
  assessment: z.looseObject({}).nullable(),
});

export type AiHealthReportResponse = z.infer<typeof AiHealthReportSchema>;
export type AiHealthCompanyResponse = z.infer<typeof AiHealthCompanySchema>;
